import { useState } from "react";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { AppAside } from "@/app-shell";
import { CELL_FIGURE_QUESTION } from "@/payroll/ai-script/ai-script-explanation-chat/selection-context";
import { AiScriptExplanationChatSidebar } from "@/payroll/ai-script/ai-script-explanation-chat-sidebar";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { WORKBOOK_FRAME } from "@/profitability/constants";
import {
  InteractiveReport,
  RowDetail,
  RowDetailProvider,
  useRowDetail,
} from "@/profitability/interactive-report";
import { ReportPresentationProvider } from "@/profitability/report-presentation";
import { ReportWorkbook } from "@/profitability/report-workbook";
import { RunDocuments } from "@/profitability/run-documents";
import type { EnrichedProfitabilityRun } from "@/profitability/types";
import { runDates } from "@/profitability/utils";
import { reportChatPath } from "@/profitability/utils";
import { DraftSampleBanner } from "./draft-sample-banner";
import { MatchedColumnsBanner } from "./matched-columns-banner";
import { RunContent } from "./run-content";
import { RunHeader } from "./run-header";
const CHAT_SURFACE = {
  title: "Job Costing chat",
  subtitle: "Chats about your job costing reports",
  emptyTitle: "Ask about this report",
  emptyDescription:
    "Find out what drove revenue and margin, by job or technician.",
  composerPlaceholder: "Ask about this report, or any other period…",
  suggestedPrompts: [
    "Summarize this report",
    "Which technicians had the lowest gross profit percent?",
    "How does this compare to the period before?",
  ],
};
const Report = ({
  businessId,
  run,
}: {
  businessId: string;
  run: EnrichedProfitabilityRun;
}) => {
  const rowDetail = useRowDetail()!;
  const [selectionContext, setSelectionContext] =
    useState<AiScriptChatSelectionContext | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { workbookReady } = run.display;

  const openRow = rowDetail.open;
  const handleChatOpenChange = (open: boolean) => {
    setIsChatOpen(open);
    if (open) rowDetail.closeRow();
    if (!open) {
      setSelectionContext(null);
      setPendingQuestion(null);
    }
  };
  const handleSidebarOpenChange = (open: boolean) => {
    if (!open) rowDetail.closeRow();
  };

  const handleAskAiAboutCell = (context: AiScriptChatSelectionContext) => {
    setSelectionContext(context);
    setPendingQuestion(CELL_FIGURE_QUESTION);
    handleChatOpenChange(true);
  };

  return (
    <SimpleSidebarLayout
      open={!!openRow}
      onOpenChange={handleSidebarOpenChange}
    >
      {/* One scroller for the whole report, the header and the tabs included,
          so they scroll away rather than pinning the content into a box of
          its own. The page frame sits inside it, so its bar rides the edge. */}
      <SimpleSidebarContent>
        <PageContainer>
          <RunHeader
            businessId={businessId}
            run={run}
            onOpenChat={() => handleChatOpenChange(true)}
          />
          {run.draft_sample && (
            <DraftSampleBanner businessId={businessId} run={run} />
          )}
          <MatchedColumnsBanner run={run} />
          <RunContent businessId={businessId} run={run}>
            {run.interactive_available ? (
              // A report that declared datasets is read as a dashboard, with the
              // grid of the same figures as its last tab. It draws its own panes,
              // so the app must not put another one around the whole thing.
              <InteractiveReport
                workbookTab="sheet"
                tabsInNav
                documentsTab={
                  <RunDocuments businessId={businessId} run={run} />
                }
                businessId={businessId}
                runId={run.id}
                dates={runDates(run)}
                run={run}
                sheet={run.workbook_available}
                onAskAiAboutCell={handleAskAiAboutCell}
              />
            ) : (
              // A report written before any of this declared nothing, so the
              <Stack
                gap={0}
                align="stretch"
                className={cn("w-full", { [WORKBOOK_FRAME]: workbookReady })}
              >
                <ReportWorkbook
                  businessId={businessId}
                  runId={run.id}
                  onAskAiAboutCell={handleAskAiAboutCell}
                />
              </Stack>
            )}
          </RunContent>
        </PageContainer>
      </SimpleSidebarContent>

      <SimpleSidebar>
        {openRow && (
          <RowDetail open={openRow} onClose={() => rowDetail.closeRow()} />
        )}
      </SimpleSidebar>
      <AppAside open={isChatOpen && !openRow}>
        <AiScriptExplanationChatSidebar
          businessId={businessId}
          runId={run.id}
          buildChatPath={reportChatPath}
          selectionContext={selectionContext}
          onClearSelectionContext={() => setSelectionContext(null)}
          pendingQuestion={pendingQuestion}
          onQuestionPrepared={() => setPendingQuestion(null)}
          title={CHAT_SURFACE.title}
          subtitle={CHAT_SURFACE.subtitle}
          emptyTitle={CHAT_SURFACE.emptyTitle}
          emptyDescription={CHAT_SURFACE.emptyDescription}
          composerPlaceholder={CHAT_SURFACE.composerPlaceholder}
          suggestedPrompts={CHAT_SURFACE.suggestedPrompts}
          acceptsImages
          onClose={() => handleChatOpenChange(false)}
        />
      </AppAside>
    </SimpleSidebarLayout>
  );
};

export const LoadedReport = (props: {
  businessId: string;
  run: EnrichedProfitabilityRun;
}) => (
  <ReportPresentationProvider value="classic">
    <RowDetailProvider>
      <Report {...props} />
    </RowDetailProvider>
  </ReportPresentationProvider>
);
